<script lang="ts">
	/**
	 * Renders the "Change view" section (List / Grid / Table) as `MenuItemList` entries
	 * intended to be dropped as children of a parent `DropdownMenu` (or a
	 * mobile options modal). Owns the persistence to `home_data.device_list.type`.
	 *
	 * The visible affordance in the header uses this component; the standalone
	 * `DeviceListToggleLayout` segmented control still ships for use inside a
	 * room context where a dedicated toggle reads better than a menu item.
	 */
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { MenuItemList } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { saveDeviceListSettings } from 'src/helpers/home_helpers';
	import type { DeviceListLayoutType } from 'src/helpers/new-layout-helpers';
	import { home_data } from 'src/stores';
	import { deviceTableViewAvailable } from 'src/stores/features/dev/device-table-view';
	import DevEnabledBadge from 'src/components/Dev/DevEnabledBadge.svelte';

	const dispatch = createEventDispatcher<{ 'item-click': void }>();

	export let disabled = false;
	/**
	 * When set, renders an "isGroupTitle" header above the view items so this
	 * section can visually stand apart from the neighbouring bulk-actions
	 * section in a shared dropdown.
	 */
	export let sectionTitle: string = '';

	$: currentView = ($home_data?.['device_list']?.type as DeviceListLayoutType) || 'default';

	const onChange = async (type: DeviceListLayoutType) => {
		if (disabled || currentView === type) return;
		const res = await saveDeviceListSettings({ type });
		onSettingsResponse(res);
	};
</script>

{#if sectionTitle}
	<MenuItemList isGroupTitle size="m" title={sectionTitle} />
{/if}

<MenuItemList
	isGroupItem
	size="m"
	title={$format('label_list_view', { default: 'List view' })}
	icon="fas fa-list"
	disabled={disabled || currentView === 'default'}
	on:click={() => {
		dispatch('item-click');
		onChange('default');
	}}
/>
<MenuItemList
	isGroupItem
	size="m"
	title={$format('label_grid_view', { default: 'Grid view' })}
	icon="fas fa-grid-2"
	disabled={disabled || currentView === 'compact'}
	on:click={() => {
		dispatch('item-click');
		onChange('compact');
	}}
/>
{#if $deviceTableViewAvailable}
	<DevEnabledBadge>
		<MenuItemList
			isGroupItem
			size="m"
			title={$format('label_table_view', { default: 'Table view' })}
			icon="fas fa-table"
			disabled={disabled || currentView === 'table'}
			on:click={() => {
				dispatch('item-click');
				onChange('table');
			}}
		/>
	</DevEnabledBadge>
{/if}
